// src/components/Header.tsx
// Updated: 2026-08-21 10:40:00 CDT
// Purpose: Compact public navigation including editor, playback settings, and authenticated account controls.

import type { User } from "@supabase/supabase-js";
import { Link, useNavigate } from "react-router-dom";
import {
  Files,
  FilePlus2,
  Film,
  LogOut,
  PanelsTopLeft,
  Settings,
  UserRound,
  UsersRound,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/auth/useAuth";
import { toast } from "sonner";
import FeedbackDialog from './FeedbackDialog';

export type PublicPage = "editor" | "scripts" | "upload" | "voices" | "settings";

interface HeaderProps {
  user?: User | null;
  activePage?: PublicPage|'account'|null;
  hasActiveScreenplay?: boolean;
  onPageChange?: (page: PublicPage) => void;
  onBeforeLeave?: () => Promise<boolean>;
}

const navigation = [
  {page:'editor',label:'Editor',icon:PanelsTopLeft,requiresScript:true},
  {page:'scripts',label:'Scripts',icon:Files,requiresScript:false},
  {page:'upload',label:'New',icon:FilePlus2,requiresScript:false},
  {page:'voices',label:'Voices',icon:UsersRound,requiresScript:true},
  {page:'settings',label:'Settings',icon:Settings,requiresScript:false},
] as const;

const Header = ({
  user,
  activePage = "editor",
  hasActiveScreenplay = false,
  onPageChange,
  onBeforeLeave,
}: HeaderProps) => {
  const { signOut } = useAuth();
  const navigate = useNavigate();

  const handleSignOut = async () => {
    try {
      if (onBeforeLeave && !(await onBeforeLeave())) return;
      await signOut();
      toast.success("Signed out successfully");
    } catch (error) {
      console.error("[Header] Sign-out failed:", error);
      toast.error("Error signing out");
    }
  };

  return (
    <header className="public-header border-b border-border bg-card/50 backdrop-blur-sm supports-backdrop-filter:bg-card/50">
      <div className="public-header-row mx-auto w-full px-4 py-[2px]">
        <div className="public-header-brand flex items-center space-x-3">
          <div className="p-2 bg-primary/10 rounded-lg border border-primary/20">
            <Film className="h-6 w-6 text-primary" />
          </div>
          <div className="public-header-brand-text flex items-baseline gap-2">
            <h1 className="text-xl font-bold">TableRead.tv</h1>
            <span className="public-header-beta font-normal text-amber-200">Beta</span>
          </div>
        </div>

        {user && (<div className="public-header-controls flex min-w-0 items-center gap-1 rounded-lg border border-border bg-background/80 p-1">
        {(
          <nav
            className="public-header-nav flex w-fit flex-nowrap items-center gap-1"
            aria-label="Primary navigation"
          >
            {navigation.map(({page,label,icon:Icon,requiresScript})=><Button key={page}
              type="button" variant={activePage===page?'secondary':'ghost'} size="sm"
              className="public-header-button" aria-label={label} title={label}
              onClick={()=>onPageChange?onPageChange(page):navigate('/',{state:{publicPage:page}})} disabled={requiresScript&&!hasActiveScreenplay}
              aria-current={activePage===page?'page':undefined}>
              <Icon aria-hidden="true" className="h-4 w-4"/><span className="public-header-label">{label}</span>
            </Button>)}
          </nav>
        )}

        {user && (
          <div className="public-header-account flex items-center gap-2">
            <Button
              asChild
              variant="ghost"
              size="sm"
              className="public-header-button"
            >
              <Link to="/account" aria-label="Account" title="Account" aria-current={activePage==='account'?'page':undefined} onClick={async (event) => {
                if (!onBeforeLeave || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
                event.preventDefault();
                if (await onBeforeLeave()) navigate("/account");
              }}>
                <UserRound aria-hidden="true" className="h-4 w-4" />
                <span className="public-header-label">Account</span>
              </Link>
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleSignOut}
              className="public-header-button" aria-label="Sign Out" title="Sign Out"
            >
              <LogOut aria-hidden="true" className="h-4 w-4" />
              <span className="public-header-label">Sign Out</span>
            </Button>
          </div>
        )}
        </div>)}
        {user&&<div className="public-header-feedback shrink-0"><FeedbackDialog header/></div>}
      </div>
    </header>
  );
};

export default Header;
